import { useEffect, useMemo, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import { useToast } from "@app/components/toast";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@app/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@app/components/ui/tabs";

import type { reviewQueuePage_Query } from "@app/queries/reviewQueuePage_Query.graphql";
import type { reviewQueuePage_ActionMutation } from "@app/queries/reviewQueuePage_ActionMutation.graphql";

import { AllRecordsTab, keyOf, type Filter, type ReviewItem } from "./all-records-tab";
import { AutoMatchedTab, type BoundRecord, type Reversal } from "./auto-matched-tab";
import { DecideTab, type Group } from "./decide-tab";
import {
  CONFIRM_ABOVE_CENTS,
  CONFIRM_ABOVE_COUNT,
  ConfirmationDialog,
  DRIFT_SENTENCE,
  VERBS,
  cents,
  type Confirmation,
} from "./shared";

// The sourcing review inbox — the last human step of QuickBooks↔bank sourcing.
// Three tabs: Decide (counterparty cards with a shape and a suggested verb),
// Auto-matched (what the pipeline linked by itself, each with undo), All records
// (filters and rows). Every verb is named by its ledger consequence, every
// decision can be taken back, and the header says what is at stake.

const pageQuery = graphql`
  query reviewQueuePage_Query {
    sourcingReview {
      totalCount
      countedSumCents
      uncountedSumCents
      unionActivated
      run {
        jobId
        status
        phase
        currentObject
        rowsProcessed
        rowsTotal
        unit
        errorMessage
        outcome {
          key
          value
        }
      }
      progress {
        since
        decidedRecords
        decidedGroups
        openGroups
        resolvedCents
      }
      groups {
        counterparty
        count
        sumCents
        withCandidates
        matchable
        counted
        countedSumCents
        uncounted
        uncountedSumCents
        shape
        recommendedAction
      }
      boundRecords {
        qboId
        entityType
        amountCents
        txnDate
        counterparty
        deltaCents
        manual
        txn {
          id
          amount
          postingDate
          description
        }
      }
      reversals {
        purchaseQboId
        depositQboId
        txnDate
        amountCents
        counterparty
        category
      }
      recentDecisions {
        id
        runId
        action
        label
        qboId
        entityType
        count
        sumCents
        decidedAt
        undoable
        expectedState
        reason
      }
      items {
        qboId
        entityType
        verdict
        amountCents
        txnDate
        counterparty
        category
        deletedInQbo
        candidates {
          txn {
            id
            amount
            postingDate
            description
          }
          exact
          deltaCents
          payee
          rivalCounterparty
          rivalDate
        }
      }
    }
    bankAccounts {
      id
      nickname
    }
  }
`;

const actionMutation = graphql`
  mutation reviewQueuePage_ActionMutation(
    $action: String!
    $items: [SourcingReviewKeyInputObject!]
    $counterparty: String
    $counterpartyPattern: String
    $bankAccountId: ID
    $periodStart: String
    $periodEnd: String
    $bankTxnId: ID
    $runId: ID
    $expectedState: String
  ) {
    sourcingReviewAction(
      action: $action
      items: $items
      counterparty: $counterparty
      counterpartyPattern: $counterpartyPattern
      bankAccountId: $bankAccountId
      periodStart: $periodStart
      periodEnd: $periodEnd
      bankTxnId: $bankTxnId
      runId: $runId
      expectedState: $expectedState
    ) {
      ok
      affectedCount
      job {
        jobId
        status
      }
      preview {
        count
        sumCents
        counted
        countedSumCents
        uncounted
        uncountedSumCents
      }
      errors {
        message
      }
    }
  }
`;

type Variables = reviewQueuePage_ActionMutation["variables"];
type Payload = NonNullable<reviewQueuePage_ActionMutation["response"]["sourcingReviewAction"]>;

const POLL_MS = 2000;
const TABS = ["decide", "matched", "all"] as const;
export type Tab = (typeof TABS)[number];

const PHASE_LABELS: Record<string, string> = {
  QUEUED: "queued",
  WAITING: "waiting its turn",
  RECORDING: "recording decisions",
  STAGING: "matching QuickBooks records",
  DERIVING: "rebuilding your books",
  ATTRIBUTING: "applying rules",
  CATEGORIZING: "categorizing",
};

const ACTION_LABELS: Record<string, string> = {
  MATCH: "Matched",
  CONFIRM: "Kept",
  DISMISS: "Excluded",
  UNBIND: "Unlinked",
  BIND: "Matched",
  RELEASE: "Undid",
};

export default function ReviewQueuePage() {
  const notify = useToast();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: Tab };
  const tab: Tab = TABS.includes(search.tab as Tab) ? (search.tab as Tab) : "decide";
  const setTab = (next: string) => navigate({ to: "/sources/review", search: { tab: next as Tab } as never, replace: true });

  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const data = useLazyLoadQuery<reviewQueuePage_Query>(pageQuery, {}, queryOptions);
  const [commitAction, actionInFlight] = useMutation<reviewQueuePage_ActionMutation>(actionMutation);

  const review = data.sourcingReview;
  const items = useMemo(() => review?.items ?? [], [review]);
  const liveRun = review?.run?.status === "running" ? review.run : null;
  const busy = actionInFlight || liveRun != null;

  // Decisions apply on a background run (the re-derive is minutes on a real
  // account); the page re-reads while one is live so the queue drains on screen.
  useEffect(() => {
    if (!liveRun) return;
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [liveRun, refresh]);

  const [expanded, setExpanded] = useState<string | null>(null); // keyed by counterparty: stable across polls
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [filter, setFilter] = useState<Filter>({ pattern: "", bankAccountId: "", periodStart: "", periodEnd: "" });
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [recentOpen, setRecentOpen] = useState(false);

  // Selection is pruned to keys still present, not cleared, on every re-read.
  useEffect(() => {
    setSelected((prev) => {
      const present = new Set(items.map(keyOf));
      const next = new Set([...prev].filter((key) => present.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [items]);

  // A mutation fails on two channels — the payload's errors, or the request
  // itself — and both must read as failure, or a rejected undo toasts success.
  const run = (variables: Variables, onDone: (payload: Payload) => { message: string; undo?: Variables } | null) =>
    new Promise<Payload | null>((resolve) => {
      commitAction({
        variables,
        onCompleted: (response, errors) => {
          const payload = response.sourcingReviewAction;
          if (payload?.ok && !errors?.length) {
            const done = onDone(payload);
            if (done) {
              notify({
                message: done.message,
                kind: "success",
                action: done.undo ? { label: "Undo", onClick: () => void run(done.undo!, () => ({ message: "Undoing in the background." })) } : undefined,
              });
            }
            refresh();
            resolve(payload);
          } else {
            notify({ message: payload?.errors[0]?.message ?? errors?.[0]?.message ?? "The action failed.", kind: "error" });
            resolve(null);
          }
        },
        onError: () => {
          notify({ message: "The action failed — please try again.", kind: "error" });
          resolve(null);
        },
      });
    });

  const started = (verb: "CONFIRM" | "DISMISS", n: number) =>
    verb === "CONFIRM"
      ? `Keeping ${n.toLocaleString()} record${n === 1 ? "" : "s"} in your books — applying in the background.`
      : `Excluding ${n.toLocaleString()} record${n === 1 ? "" : "s"} from your books — applying in the background.`;

  // R11: a big action asks first. Group cards carry their own numbers; a filter
  // action previews server-side (no writes) before the dialog can show real ones.
  const guarded = (verb: "CONFIRM" | "DISMISS", label: string, numbers: Omit<Confirmation, "verb" | "label" | "proceed">, proceed: () => void) => {
    if (Math.abs(numbers.sumCents) > CONFIRM_ABOVE_CENTS || numbers.count > CONFIRM_ABOVE_COUNT) {
      setConfirmation({ verb, label, ...numbers, proceed });
    } else {
      proceed();
    }
  };

  const actOnGroup = (verb: "CONFIRM" | "DISMISS" | "MATCH", group: Group) => {
    if (verb === "MATCH") {
      // Binding is evidence-driven and reversible per record (Auto-matched → "Not
      // the same payment"), so it never asks first.
      void run({ action: "MATCH", counterparty: group.counterparty }, (payload) => ({
        message: `Matching ${payload.affectedCount.toLocaleString()} record${payload.affectedCount === 1 ? "" : "s"} to their twins in the background.`,
      }));
      return;
    }
    const proceed = () =>
      void run({ action: verb, counterparty: group.counterparty }, (payload) => ({
        message: started(verb, payload.affectedCount),
        undo: payload.job ? { action: "RELEASE", runId: String(payload.job.jobId) } : undefined,
      }));
    guarded(
      verb,
      group.counterparty,
      {
        count: group.count,
        sumCents: group.sumCents,
        counted: group.counted,
        countedSumCents: group.countedSumCents,
        uncounted: group.uncounted,
        uncountedSumCents: group.uncountedSumCents,
      },
      proceed,
    );
  };

  const filterVariables = (): Variables => ({
    action: "PREVIEW",
    counterpartyPattern: filter.pattern === "" ? null : filter.pattern,
    bankAccountId: filter.bankAccountId === "" ? null : filter.bankAccountId,
    periodStart: filter.periodStart === "" ? null : filter.periodStart,
    periodEnd: filter.periodEnd === "" ? null : filter.periodEnd,
  });

  const actOnFilter = async (verb: "CONFIRM" | "DISMISS") => {
    const preview = await run(filterVariables(), () => null);
    if (!preview?.preview) return;
    const numbers = preview.preview;
    const label = [filter.pattern && `"${filter.pattern}"`, (filter.periodStart || filter.periodEnd) && `${filter.periodStart || "…"} – ${filter.periodEnd || "…"}`]
      .filter(Boolean)
      .join(" · ");
    guarded(verb, label || "the filtered records", numbers, () =>
      void run({ ...filterVariables(), action: verb }, (payload) => ({
        message: started(verb, payload.affectedCount),
        undo: payload.job ? { action: "RELEASE", runId: String(payload.job.jobId) } : undefined,
      })),
    );
  };

  const actOnSelected = (verb: "CONFIRM" | "DISMISS") => {
    const keys = items.filter((item) => selected.has(keyOf(item))).map((item) => ({ qboId: item.qboId, entityType: item.entityType }));
    void run({ action: verb, items: keys }, (payload) => ({
      message: started(verb, payload.affectedCount),
      undo: payload.job ? { action: "RELEASE", runId: String(payload.job.jobId) } : undefined,
    }));
  };

  const bind = (item: { qboId: string; entityType: string }, bankTxnId: string) =>
    void run({ action: "BIND", items: [{ qboId: item.qboId, entityType: item.entityType }], bankTxnId }, () => ({
      message: "Matched — the bank row is now the event.",
      undo: { action: "UNBIND", items: [{ qboId: item.qboId, entityType: item.entityType }] },
    }));

  const notReversal = (reversal: Reversal) =>
    void run(
      {
        action: "NOT_REVERSAL",
        items: [
          { qboId: reversal.purchaseQboId, entityType: "Purchase" },
          { qboId: reversal.depositQboId, entityType: "Deposit" },
        ],
      },
      () => ({ message: "Keeping both records in your books — applying in the background." }),
    );

  const unbind = (record: BoundRecord) =>
    void run({ action: "UNBIND", items: [{ qboId: record.qboId, entityType: record.entityType }] }, () => ({
      message: "Unlinking in the background — the record comes back as its own cost.",
    }));

  const undo = (entry: NonNullable<typeof review>["recentDecisions"][number]) => {
    if (entry.action === "BIND" && entry.qboId && entry.entityType) {
      void run({ action: "UNBIND", items: [{ qboId: entry.qboId, entityType: entry.entityType }] }, () => ({
        message: "Unlinking in the background.",
      }));
    } else if (entry.runId) {
      void run({ action: "RELEASE", runId: entry.runId }, (payload) => ({
        message: `Undoing ${payload.affectedCount.toLocaleString()} decision${payload.affectedCount === 1 ? "" : "s"} in the background.`,
      }));
    }
  };

  const retry = (runId: string) => void run({ action: "RETRY", runId }, () => ({ message: "Running the same decisions again." }));
  const startClose = () => void run({ action: "START_CLOSE" }, () => ({ message: "A new close starts now." }));

  if (review == null) return null; // gated, or no account context

  const clean = review.totalCount === 0;
  const progress = review.progress;
  const groupsTotal = progress.decidedGroups + progress.openGroups;

  return (
    <div className="max-w-6xl space-y-4 p-6">
      <header className="space-y-2">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Review</h1>
            <p className="text-sm text-gray-500">
              <Link to="/sources" className="text-indigo-600 underline">
                Back to sources
              </Link>
              {refreshing && <span className="ml-2 text-xs text-gray-400">Updating…</span>}
            </p>
          </div>
          {!clean && (
            <p className="text-right text-xs text-gray-500">
              This close: {progress.decidedGroups.toLocaleString()} of {groupsTotal.toLocaleString()} counterparties decided ·{" "}
              {progress.decidedRecords.toLocaleString()} records · {cents(progress.resolvedCents)} resolved
              {progress.since == null && " (since ever)"}
              {" · "}
              <button type="button" onClick={startClose} disabled={busy} className="underline hover:no-underline disabled:opacity-50">
                Start a new close
              </button>
            </p>
          )}
        </div>

        {review.unionActivated && !clean && (
          <p className="text-sm text-gray-800">
            <span className="font-medium">{review.totalCount.toLocaleString()} QuickBooks records</span> have no bank or wallet
            transaction behind them. {cents(review.countedSumCents)} of that is counted in your books today;{" "}
            {cents(review.uncountedSumCents)} is not. Each one is either a real cost (Keep), the same payment as a bank row (Match),
            or noise (Exclude).
          </p>
        )}

        <Collapsible>
          <CollapsibleTrigger className="text-xs text-indigo-600 underline hover:no-underline">How this works</CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-1 rounded border bg-white px-4 py-3 text-sm text-gray-700">
              <p>
                <span className="font-medium">{VERBS.BIND.label}</span> — {VERBS.BIND.consequence}.
              </p>
              <p>
                <span className="font-medium">{VERBS.CONFIRM.label}</span> — {VERBS.CONFIRM.consequence}.
              </p>
              <p>
                <span className="font-medium">{VERBS.DISMISS.label}</span> — {VERBS.DISMISS.consequence}.
              </p>
              <p>
                {DRIFT_SENTENCE} Matches the pipeline makes by itself, and QuickBooks void pairs (a Purchase and a Deposit that cancel each
                other) it nets to zero, are listed under Auto-matched, each with its undo.
              </p>
              <p>
                Every decision can be taken back from "Recently decided". The runbook:{" "}
                <code className="text-xs">docs/runbooks/qbo-near-twin-cleanup.md</code>; the health check:{" "}
                <Link to="/data-health" className="text-indigo-600 underline">
                  /data-health
                </Link>{" "}
                (C8).
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </header>

      {/* The review run: decisions land on a worker and re-derive once. Live it
          narrates; failed it says why and offers Retry; done it says what it decided. */}
      {review.run && (review.run.status === "running" || review.run.status === "error") && (
        <div
          className={`rounded border px-4 py-3 text-sm ${
            review.run.status === "error" ? "border-red-200 bg-red-50 text-red-800" : "border-indigo-200 bg-indigo-50 text-indigo-900"
          }`}
        >
          {review.run.status === "running" ? (
            <p>
              <span className="font-medium">Applying decisions…</span> {review.run.currentObject ?? PHASE_LABELS[review.run.phase ?? ""] ?? "queued"}
              {review.run.rowsTotal ? (
                <span className="tabular-nums text-indigo-600">
                  {" "}
                  · {review.run.rowsProcessed.toLocaleString()} / {review.run.rowsTotal.toLocaleString()} {review.run.unit ?? ""}
                </span>
              ) : null}
            </p>
          ) : (
            <p className="flex flex-wrap items-center gap-3">
              <span>
                <span className="font-medium">The last decisions did not finish:</span> {review.run.errorMessage || "unknown error"} — nothing it had
                not finished was applied.
              </span>
              <button
                type="button"
                disabled={actionInFlight}
                onClick={() => retry(String(review.run!.jobId))}
                className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-800 hover:bg-red-100 disabled:opacity-50"
              >
                Retry
              </button>
            </p>
          )}
        </div>
      )}

      {review.recentDecisions.length > 0 && (
        <section className="rounded border bg-white">
          <button type="button" onClick={() => setRecentOpen((open) => !open)} className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm">
            <span className="font-medium text-gray-900">Recently decided</span>
            <span className="text-xs text-gray-500">{review.recentDecisions.length.toLocaleString()} this close — each can be taken back</span>
            <span className="ml-auto text-xs text-indigo-600 underline">{recentOpen ? "hide" : "show"}</span>
          </button>
          {recentOpen && (
            <ul className="divide-y border-t">
              {review.recentDecisions.map((entry) => (
                <li key={entry.id} className="flex flex-wrap items-center gap-3 px-4 py-2 text-xs text-gray-700">
                  <span className="tabular-nums text-gray-500">{entry.decidedAt ? new Date(entry.decidedAt).toLocaleString() : "—"}</span>
                  <span className="font-medium">{ACTION_LABELS[entry.action] ?? entry.action}</span>
                  <span className="max-w-[20rem] truncate">{entry.label || "(no counterparty)"}</span>
                  <span className="text-gray-500">
                    {entry.count.toLocaleString()} record{entry.count === 1 ? "" : "s"} · <span className="tabular-nums">{cents(entry.sumCents)}</span>
                  </span>
                  {entry.undoable ? (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => undo(entry)}
                      title={entry.action === "BIND" ? VERBS.UNBIND.consequence : VERBS.RELEASE.consequence}
                      className="ml-auto rounded border px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {entry.action === "BIND" ? VERBS.UNBIND.label : VERBS.RELEASE.label}
                    </button>
                  ) : (
                    <span className="ml-auto text-gray-400">{entry.reason}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {!review.unionActivated ? (
        <section className="rounded border bg-white px-4 py-6 text-sm text-gray-600">
          QuickBooks sourcing is not active for this account yet, so there is nothing to review. Connect and sync QuickBooks on{" "}
          <Link to="/sources" className="text-indigo-600 underline">
            Sources
          </Link>{" "}
          first.
        </section>
      ) : clean && review.boundRecords.length === 0 && review.reversals.length === 0 ? (
        <section className="rounded border bg-white px-4 py-6 text-sm text-gray-600">
          Nothing to review — every QuickBooks record is backed by a bank or wallet transaction, kept, or excluded. New records arrive
          with the next QuickBooks sync or statement upload.
        </section>
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="decide" count={review.groups.length}>
              Decide
            </TabsTrigger>
            <TabsTrigger value="matched" count={review.boundRecords.length + review.reversals.length}>
              Auto-matched
            </TabsTrigger>
            <TabsTrigger value="all" count={review.totalCount}>
              All records
            </TabsTrigger>
          </TabsList>
          <TabsContent value="decide">
            {clean ? (
              <p className="text-sm text-gray-400">Nothing left to decide.</p>
            ) : (
              <DecideTab
                groups={review.groups}
                expanded={expanded}
                onExpand={setExpanded}
                fetchKey={queryOptions.fetchKey}
                busy={busy}
                onGroupAction={actOnGroup}
                onBind={bind}
              />
            )}
          </TabsContent>
          <TabsContent value="matched">
            <AutoMatchedTab records={review.boundRecords} reversals={review.reversals} busy={busy} onUnbind={unbind} onNotReversal={notReversal} />
          </TabsContent>
          <TabsContent value="all">
            <AllRecordsTab
              items={items as ReadonlyArray<ReviewItem>}
              totalCount={review.totalCount}
              bankAccounts={data.bankAccounts}
              filter={filter}
              onFilter={setFilter}
              selected={selected}
              onSelect={setSelected}
              busy={busy}
              onFilterAction={(verb) => void actOnFilter(verb)}
              onSelectedAction={actOnSelected}
              onBind={bind}
            />
          </TabsContent>
        </Tabs>
      )}

      <ConfirmationDialog confirmation={confirmation} onClose={() => setConfirmation(null)} />
    </div>
  );
}
